// Fonte única dos itens de navegação principal + RBAC. Consumido pela Sidebar
// (desktop, mostra todos os itens do papel) e pela BottomNav/MoreSheet (mobile,
// particiona em primários no rodapé + overflow no "Mais").
import { ROUTES } from '@/app/router/paths';
import {
  AnalyticsIcon,
  BriefcaseIcon,
  FlameIcon,
  HomeIcon,
  InboxIcon,
  PackageIcon,
  SparklesIcon,
  StoreIcon,
  TargetIcon,
  TeamIcon,
} from '@/components/icons';
import type { ReactElement } from 'react';
import { PDV_ROLES, PRODUCTION_ROLES, type Role } from '@/config/constants';
import type { IconProps } from '@/components/icons';

export interface NavItem {
  to: string;
  label: string;
  /** Rótulo curto pra BottomNav (tab estreita). Cai em `label` se ausente. */
  shortLabel?: string;
  Icon: (p: IconProps) => ReactElement;
  /** Roles que podem ver o item. */
  roles: Role[];
  /** Aparece como tab no rodapé mobile; os demais vão pro "Mais". */
  mobilePrimary?: boolean;
}

const EVERYONE: Role[] = ['GESTOR', 'GERENTE', 'VENDEDOR', 'ATENDENTE'];
const ALL: Role[] = ['GESTOR', 'GERENTE', 'VENDEDOR'];
const MANAGERS: Role[] = ['GESTOR', 'GERENTE'];

/** Máximo de tabs (fora o "Mais") que cabem no rodapé a 360px. */
export const MOBILE_PRIMARY_LIMIT = 4;

// Empresas = painel de rede (gestor/gerente) com KPIs consolidados,
// catálogo/IA/equipe por loja. Ajustes/Sair vivem no rodapé da Sidebar e no
// MoreSheet (não são itens de navegação primária).
export const NAV_ITEMS: NavItem[] = [
  {
    to: ROUTES.dashboard,
    label: 'Minha visão',
    Icon: HomeIcon,
    roles: EVERYONE,
    mobilePrimary: true,
  },
  { to: ROUTES.inbox, label: 'Conversas', Icon: InboxIcon, roles: ALL, mobilePrimary: true },
  { to: ROUTES.contacts, label: 'Clientes', Icon: TeamIcon, roles: ALL, mobilePrimary: true },
  {
    to: ROUTES.crm,
    label: 'CRM Clientes',
    shortLabel: 'CRM',
    Icon: BriefcaseIcon,
    roles: ALL,
    mobilePrimary: true,
  },
  // PDV presencial (RF-01). O atendente vê só Minha visão · Guichê · Produtos.
  { to: ROUTES.guiche, label: 'Guichê', Icon: StoreIcon, roles: PDV_ROLES, mobilePrimary: true },
  {
    to: ROUTES.produtos,
    label: 'Produtos',
    Icon: PackageIcon,
    roles: ['ATENDENTE'],
    mobilePrimary: true,
  },
  // Produção (RF-04). `mobilePrimary` porque é o ÚNICO item do papel PRODUCAO — sem
  // ele o rodapé mobile ficaria vazio e a tela só existiria dentro do "Mais". Para
  // gestor/gerente o item cai depois do corte de MOBILE_PRIMARY_LIMIT, então o
  // rodapé deles não muda.
  {
    to: ROUTES.producao,
    label: 'Produção',
    Icon: FlameIcon,
    roles: PRODUCTION_ROLES,
    mobilePrimary: true,
  },
  { to: ROUTES.campanhas, label: 'Campanhas', Icon: SparklesIcon, roles: ALL },
  // Indicadores (MET-01..MET-28). `ALL` inclui o VENDEDOR: MET-27 exige que ele
  // veja os próprios números, e o recorte é feito por clamp no selector. Sem
  // `mobilePrimary` — cai no "Mais", como Campanhas/Metas/Empresas.
  { to: ROUTES.indicadores, label: 'Indicadores', Icon: AnalyticsIcon, roles: ALL },
  { to: ROUTES.metas, label: 'Metas', Icon: TargetIcon, roles: MANAGERS },
  { to: ROUTES.stores, label: 'Empresas', Icon: StoreIcon, roles: MANAGERS },
];

/** Itens visíveis pro papel (todos os do papel, ordem da Sidebar). */
export function navItemsForRole(role: Role): NavItem[] {
  return NAV_ITEMS.filter((it) => it.roles.includes(role));
}

/**
 * Partição do menu mobile: até MOBILE_PRIMARY_LIMIT tabs no rodapé, o resto no
 * "Mais". O corte é aqui (e não em cada consumidor) pra BottomNav e MoreSheet
 * nunca divergirem — um item fora do rodapé tem que estar no sheet.
 */
export function mobileNavForRole(role: Role): { primary: NavItem[]; overflow: NavItem[] } {
  const items = navItemsForRole(role);
  const primary = items.filter((it) => it.mobilePrimary).slice(0, MOBILE_PRIMARY_LIMIT);
  const overflow = items.filter((it) => !primary.includes(it));
  return { primary, overflow };
}
